// components/preferences/EmailPreferences.tsx
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../../../services/api';

/**
 * News / event email subscription toggle.
 * Saves immediately on its own endpoint, independent of the profile Save button.
 */
export const EmailPreferences: React.FC = () => {
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let alive = true;
    apiFetch('/api/marketing/subscription/')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => alive && setSubscribed(Boolean(data.subscribed)))
      .catch(() => alive && setStatus({ kind: 'error', text: 'Could not load your email preferences.' }));
    return () => {
      alive = false;
    };
  }, []);

  const toggle = async () => {
    if (subscribed === null || saving) return;
    const next = !subscribed;
    setSaving(true);
    setStatus(null);
    try {
      const r = await apiFetch('/api/marketing/subscription/', {
        method: 'PUT',
        body: JSON.stringify({ subscribed: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setSubscribed(Boolean(data.subscribed));
      setStatus({
        kind: 'ok',
        text: data.subscribed ? "You're subscribed to news and event emails." : "You've been unsubscribed.",
      });
    } catch {
      setStatus({ kind: 'error', text: 'Could not update your preference. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">Email Preferences</h2>
        <p className="text-sm text-gray-600">Choose which emails you'd like to receive.</p>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="text-sm font-medium text-gray-900">News and event updates</p>
            <p className="text-sm text-gray-500 mt-0.5">
              Occasional emails about SDG events, new features and activities. Account emails such as
              verification codes are always sent.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={subscribed === true}
            aria-label="News and event updates"
            onClick={toggle}
            disabled={subscribed === null || saving}
            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              subscribed ? 'bg-blue-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                subscribed ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
        {status && (
          <p className={`text-sm mt-3 ${status.kind === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{status.text}</p>
        )}
      </div>
    </div>
  );
};
